import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * ResearchMappingPage Component
 * 
 * Displays research mapping tools content like the legacy HTML site
 */
const ResearchMappingPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Research Mapping intro content from the legacy HTML
  const researchMappingIntroHtml = `
    <p>
    The large volumes of research so far will feel loosely connected, but also random and even disparate - we can begin to tie it all together visually to spot themes, commonalties and ultimately opportunities.
    </p>

    <p>
    The trends and other insights that come from this ultimately help us make better decisions when moving forwards, which enables us to revisit the original decision when we iterate.
    </p>
  `;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Templates HTML for each tool
  const summarisingResearchTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/research-mapping/summarising-research.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koGnCx8=/" className="link-table">Miro</a>
    </>
  );

  const affinityMapTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/research-mapping/affinity-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koG53mo=/" className="link-table">Miro</a>
    </>
  );

  const conceptMappingTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/research-mapping/concept-mapping.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koFdq4Y=/" className="link-table">Miro</a>
    </>
  );

  const assumptionsMapTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/mapping/research-mapping/assumptions-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koIzr6o=/" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const naCreator = <a href="">N/A</a>;
  const jiroKawakitaCreator = <a href="https://en.wikipedia.org/wiki/Affinity_diagram">Jiro Kawakita</a>;
  const josephNovakCreator = <a href="https://en.wikipedia.org/wiki/Concept_map">Joseph D. Novak</a>;
  const davidBlandCreator = <a href="https://www.mural.co/blog/intro-assumptions-mapping">David J Bland</a>;

  return (
    <>
      {/* Research Mapping Intro */}
      <div id="section-tools-research-mapping" className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/research#mapping">Mapping</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/research/mapping/research-mapping">Research Mapping</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={researchMappingIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#summarising-research">Summarising Research</Link></div>
            <div><Link className="tool-section-cta" to="#affinity-map">Affinity Map</Link></div>
            <div><Link className="tool-section-cta" to="#concept-mapping">Concept Mapping</Link></div>
            <div><Link className="tool-section-cta" to="#assumptions-map">Assumptions Map</Link></div>
          </div>
        </div>
      </div>

      {/* Summarising Research Section */}
      <main className="case-study">
        <span className="case-tool-title" id="summarising-research">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="summarising-research">
            <span className="tool-title-number">
              <Link to="/research/mapping/research-mapping#summarising-research">Summarising Research</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research#mapping">Mapping</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/mapping/research-mapping">Research Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/research-mapping#summarising-research"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-summarising-research" 
            id="tab-summarising-research-overview" 
            defaultChecked
          />
          <label htmlFor="tab-summarising-research-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Research Mapping", "21/08/20", summarisingResearchTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                As we come out of the research phase, we would have collected a lot of fascinating insights and valuable data through the various methods used. 
                </p>
                
                <p>
                It is helpful to collect the outcome of this research in a single place that can be referred back to, adding as many insights as possible that can then be used to further understand and evaluate the problem, lining us up for ideation and hopefully finding a solution.
                </p>
                
                <p>
                This also acts as a valuable way of aligning stakeholders as to the research and showing the true value of this resource intensive process. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              {/* No further resources for this tool in the legacy HTML */}
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-summarising-research" 
            id="tab-summarising-research-template"
          />
          <label htmlFor="tab-summarising-research-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koGnCx8=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Summarising Research Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Affinity Map Section */}
      <main className="case-study">
        <span className="case-tool-title" id="affinity-map">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="affinity-map">
            <span className="tool-title-number">
              <Link to="/research/mapping/research-mapping#affinity-map">Affinity Map</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research#mapping">Mapping</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/mapping/research-mapping#affinity-map">Research Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/research-mapping#affinity-map"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-affinity-map" 
            id="tab-affinity-map-overview" 
            defaultChecked
          />
          <label htmlFor="tab-affinity-map-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Research Mapping", "21/08/20", affinityMapTemplates, jiroKawakitaCreator)}
              <div className="tool-opening">
                <p>
                With lots of data and insights obtained during the research phase, it can seem a bit disparate and random. Using an Affinity Map, we can begin to group this research output and spot patterns - interpreting things from the perspective of the user.
                </p>
                
                <p>
                This is a highly valuable approach when it comes to analysing that such a problem exists, and the size of the problem. It can also provide insight in to a different problem or taking the project in a slightly different direction, if for example the problem is slightly different to the initial assumptions.  
                </p>
                
                <p>
                Using a collaborative approach through a workshop, we can take each individual insight and other research point and keeping it brief, put it on a single sticky note. 
                </p>
                
                <p>
                From here we can plot the sticky notes and group them to identify themes and stack those that are similar to help build priority and importance around the point.  
                </p>    

                <p>
                <span className="press">Note:</span> We can use Affinity Mapping for a variety of other Product and Design related tasks, like grouping similar ideas or usability feedback for example.  
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/affinity-diagram/" className="link-table">Affinity Diagramming for Collaboratively Sorting UX Findings and Design Ideas</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/affinity-diagrams-learn-how-to-cluster-and-bundle-ideas-and-facts" className="link-table">Affinity Diagrams – Learn How to Cluster and Bundle Ideas and Facts</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-affinity-map" 
            id="tab-affinity-map-template"
          />
          <label htmlFor="tab-affinity-map-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koG53mo=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Affinity Map Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Concept Mapping Section */}
      <main className="case-study">
        <span className="case-tool-title" id="concept-mapping">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="concept-mapping">
            <span className="tool-title-number">
              <Link to="/research/mapping/research-mapping#concept-mapping">Concept Mapping</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research#mapping">Mapping</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/mapping/research-mapping#concept-mapping">Research Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/research-mapping#concept-mapping"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-concept-mapping" 
            id="tab-concept-mapping-overview" 
            defaultChecked
          />
          <label htmlFor="tab-concept-mapping-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Research Mapping", "21/08/20", conceptMappingTemplates, josephNovakCreator)}
              <div className="tool-opening">
                <p>
                Concept Mapping aims to connect insights that have a commonality that lead to an idea or product feature. By organising and structuring existing knowledge along with research around the problem space it helps with creating concepts that can help with understanding. 
                </p>
                
                <p>
                Perfect as a workshop to build consensus and unity amongst teams, iteration is encouraged to clarify and get closer to accuracy, it enables individuals and teams to become a lot more knowledgeable about a thing a lot quicker. 
                </p>
                
                <p>
                Taking two words (concepts), and then linking them together with a relevant word creates a 'proposition' - as we move forward linking and cross-linking, this map becomes a hierarchy of meaning and therefore a rapid amount of knowledge on the concepts. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/cognitive-mind-concept/" className="link-table">Cognitive Maps, Mind Maps, and Concept Maps: Definitions</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://beingagile.co.uk/sailboat-retrospective-step-by-step-guide-workshop-video-and-examples-gallery-being-agile/" className="link-table">The Origin and Development of Concept Maps</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://conceptmappingtogether.com" className="link-table">Concept Mapping Together</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-concept-mapping" 
            id="tab-concept-mapping-template"
          />
          <label htmlFor="tab-concept-mapping-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koFdq4Y=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Concept Mapping Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Assumptions Map Section */}
      <main className="case-study">
        <span className="case-tool-title" id="assumptions-map">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="assumptions-map">
            <span className="tool-title-number">
              <Link to="/research/mapping/research-mapping#assumptions-map">Assumptions Map</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research#mapping">Mapping</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/mapping/research-mapping#assumptions-map">Research Mapping</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/research/mapping/research-mapping#assumptions-map"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-assumptions-map" 
            id="tab-assumptions-map-overview" 
            defaultChecked
          />
          <label htmlFor="tab-assumptions-map-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Research Mapping", "21/08/20", assumptionsMapTemplates, davidBlandCreator)}
              <div className="tool-opening">
                <p>
                One of the best parts of product development is building the picture, envisaging what you want to build and how it fits in to the grand scheme of things, all with the assumption enough users want it.
                </p>
                
                <p>
                Unfortunately, these assumptions can steer us in the wrong direction, resulting in wasted time and resource. However, an Assumptions Map can help deconstruct these assumptions which can prove valuable when it comes to experimenting and prototyping.  
                </p>
                
                <p>
                Focusing on the key assumptions of Desirability (do they want it?) Viability (should we do this?) and Feasibility (can we do this?), we can map and test our assumptions as a team, pushing us to check that our existing plan is valid and also creating a list of future exploratory areas. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://designsprintkit.withgoogle.com/methodology/phase2-define/assumptions-mapping" className="link-table">Assumptions Mapping</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.mural.co/blog/intro-assumptions-mapping" className="link-table">An Introduction to Assumptions Mapping with David J. Bland: Webinar Recap</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-assumptions-map" 
            id="tab-assumptions-map-template"
          />
          <label htmlFor="tab-assumptions-map-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koIzr6o=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Assumptions Map Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default ResearchMappingPage; 